/* eslint-disable max-len */
import { Arr } from '@deqo/foundation';
import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { useDraft } from '../../../../CodesThatsGottaGoSomewhereElse';
import { ApiClient } from '../../../../api/ApiClient';
import { v2 } from '../../../../api/ApiInterfaceV2';
import { BoardLiteData } from '../../../../api/ApiTypes';
import { Icon } from '../../../../concepts/resources/Icon';
import { useSubmitter } from '../../../../platform/Hook';
import { Button, Colors, Input, TypeaheadItem, usePrompt } from '../../../../ui';
import { DropButton } from '../../../../ui/DropButton';
import { ResultInfoForSubmitter } from '../../../../ui/ResultInfo';
import { useResolve } from '../../../Container';
import { ProfileModel } from '../../../ProfileModel';
import { BoardItem } from '../../../common/BoardItem';
import { Popup, PopupProps } from '../../../popups/Popup';
import { useBoardThumb } from '../../elements/BoardThumb';
import { ButtonStrip, Flexer, Spacer } from '../../elements/LayoutElements';
import { TemplateTagInput } from './TemplateTagInput';

const adjs = ['beautiful', 'cool', 'dazzling', 'fabulous', 'gorgeous', 'spicy', 'swanky', 'tremendous'];

const Frame = styled.div`
    display: flex;
    flex-direction: row;
    gap: 2rem;
    padding: 0rem;
`;

const FormFrame = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: 32rem;
    padding: 0.5rem 0;

    ${DropButton} {
        cursor: pointer;
    }

    ${Input.Label} {
        color: ${Colors.text};
    }
`;

export type TemplateCreatePopupAction =
    ['cancel', null] |
    ['restart', null] |
    ['complete', { executionId: string }];

type OnAction = (action: TemplateCreatePopupAction) => void;

const PreviewBoardItem = ({ board, clickable, onClick }: { board: BoardLiteData, clickable: boolean, onClick(): void }) => {

    const thumbUrl = useBoardThumb(board);

    return <Flexer>
        <BoardItem key={board.id} width={24} {...{ clickable, onClick }}>
            <BoardItem.Header>
                <BoardItem.Label title={board.label}>
                    {board.label}
                </BoardItem.Label>
            </BoardItem.Header>
            <BoardItem.Main>
                <BoardItem.Image>
                    <BoardItem.Thumb src={thumbUrl} />
                </BoardItem.Image>
            </BoardItem.Main>
        </BoardItem>
    </Flexer>;
};

const TemplateCreateForm = ({ board, canChangeBoard, onAction }: { board: BoardLiteData, canChangeBoard: boolean, onAction: OnAction }) => {

    const api = useResolve(ApiClient);
    const me = useResolve(ProfileModel);

    const [draft, patchDraft] = useDraft({
        name: '',
        desc: `A ${Arr.random(adjs)} Ludi template by ${me.name}.`,
        tags: [] as TypeaheadItem[],
    });

    const [errors, setErrors] = useState({
        any: false,
        name: false,
        desc: false,
        tags: false,
    });

    const draftValidate = useCallback(() => {
        const name = !draft.name.trim();
        const desc = !draft.desc.trim();
        const tags = !draft.tags.length;
        setErrors({
            any: name || desc || tags,
            name, desc, tags
        });
        return !name && !desc;
    }, [draft]);

    const [onSubmit, working, result] = useSubmitter(async () => {
        return await api.exec(v2.templates.create2({
            source: board.id,
            name: draft.name,
            desc: draft.desc,
            tags: draft.tags.map(x => x.id),
        }));
    }, [board, draft, draftValidate]);

    const onPublish = useCallback(async () => {
        if (working) return;
        if (!draftValidate()) return;
        const resp = await onSubmit();
        onAction(['complete', resp]);
    }, [working, onSubmit, onAction]);

    const onCancel = useCallback(() => {
        onAction(['cancel', null]);
    }, [onAction]);

    const onRestart = useCallback(() => {
        onAction(['restart', null]);
    }, [onAction]);

    const onPrompt = useCallback((confirm: boolean) => {
        if (confirm) {
            onPublish();
        }
        else {
            onCancel();
        }
    }, [onPublish, onCancel]);

    const promptRef = usePrompt({ enterRequiresCtrl: true, onAction: onPrompt, });

    return <Frame ref={promptRef}>
        <Flexer>
            <PreviewBoardItem board={board} clickable={canChangeBoard} onClick={onRestart} />
        </Flexer>
        <FormFrame>
            <Input
                label="Name:"
                value={draft.name}
                error={errors.name}
                onChange={x => patchDraft({ name: x })} />
            <Input
                label="Description:"
                value={draft.desc}
                error={errors.desc}
                multiline={{ rows: 4, resize: 'both' }}
                onChange={x => patchDraft({ desc: x })} />
            <Flexer>
                <TemplateTagInput error={errors.tags} onChange={x => {
                    patchDraft({ tags: x });
                }} />
            </Flexer>
            <ResultInfoForSubmitter result={result} />
            <ButtonStrip align="right">
                <Button variant="primary" onAction={onPublish}>Publish</Button>
                <Button variant="common" onAction={onCancel}>Cancel</Button>
            </ButtonStrip>
        </FormFrame>
    </Frame>;
};

export const templateCreatePopup = (board: BoardLiteData, canChangeBoard: boolean = true) => ({ ui: TemplateCreatePopup, args: { board, canChangeBoard } });
export const TemplateCreatePopup = ({ action, dismiss, args }: PopupProps) => {
    const board = args.board as BoardLiteData;
    const canChangeBoard = args.canChangeBoard;

    const onAction = useCallback((result: TemplateCreatePopupAction) => {
        action(result);
    }, [action, dismiss]);

    return <Popup p="2">
        <Popup.Content>
            <Popup.Header>
                <Popup.IconFrame>
                    <Icon src="deqo://icons/deqo/design-publish" size="2" />
                </Popup.IconFrame>
                <Popup.Title>
                    New Template
                </Popup.Title>
                <Popup.SubTitle>
                    Create a template from an existing board.
                </Popup.SubTitle>
            </Popup.Header>
            <Spacer f={1} />
            <TemplateCreateForm {...{ board, canChangeBoard, onAction }} />
        </Popup.Content>
        <Popup.Close onAction={dismiss} />
    </Popup>;
};
